import React, { useState } from 'react';
import { Button, Icon, Modal } from 'Components';
import {
	Course,
	CourseDetailModalProps,
	Language
} from 'LearningManager/constants/createAssignment.constant';
import styles from './CourseDetailModal.module.scss';
import bannerImg from 'LearningManager/images/course-1.png';
import classNames from 'classnames';

interface Props extends CourseDetailModalProps {
	visible: boolean;
	onClose: () => void;
}

interface MetaInfoProps {
	title: string;
	value: string;
	icon: React.ReactNode;
}

interface AccordionProps {
	course: Course;
	expandedCourseId: string | null;
	toggleCourse: (id: string) => void;
}

const MetaInfoCard: React.FC<MetaInfoProps> = ({ title, value, icon }) => {
	return (
		<div className={styles.card}>
			<div className={styles.label}>
				{icon}
				<span className={styles.head}>{title}</span>
			</div>
			<span className={styles.value}>{value}</span>
		</div>
	);
};

const Accordion = ({
	course,
	expandedCourseId,
	toggleCourse
}: AccordionProps) => {
	const isExpanded = expandedCourseId === course.id;

	return (
		<div key={course.id} className={styles.courseItem}>
			<div
				className={classNames(styles.accordion, {
					[styles['accordion--expanded']]: isExpanded
				})}
				onClick={() => toggleCourse(course.id)}
			>
				<span className={styles.number}>{course.number}</span>

				<div className={styles.label}>
					<div className={styles.accTitle}>{course.title}</div>
					<div className={styles.subtitle}>{course.subtitle}</div>
				</div>

				<Icon
					type="chevronUp"
					role="img"
					className={styles.chevron}
					style={{
						rotate: isExpanded ? '0deg' : '180deg'
					}}
				/>
			</div>

			<div
				className={classNames(styles.accordionContent, {
					[styles['accordionContent--expanded']]: isExpanded
				})}
			>
				<div className={styles.accordionBody}>
					<div>
						<div className={styles.bodyTitle}>Description</div>
						<div className={styles.bodyDescription}>
							{course.description}
						</div>
					</div>

					<div className={styles.meta}>
						<MetaInfoCard
							title="Run time"
							icon={<Icon type="durationIcon" role="img" />}
							value={course.runTime}
						/>
						<MetaInfoCard
							title="Created"
							icon={<Icon type="createdIcon" role="img" />}
							value={course.created}
						/>
					</div>

					<div className={styles.languages}>
						<div className={styles.languages__label}>
							Available languages
						</div>
						<div className={styles.languages__tags}>
							{course.languages.map((lang) => (
								<LanguageTag lang={lang} />
							))}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

const LanguageTag = ({ lang }: { lang: Language }) => {
	return (
		<span key={lang.label} className={styles.badge}>
			{lang.label}
		</span>
	);
};

const CourseDetailModal: React.FC<Props> = ({
	visible,
	onClose,
	title,
	courseId,
	description,
	runTime,
	created,
	languages,
	courses
}) => {
	const [expandedCourseId, setExpandedCourseId] = useState<string | null>(
		null
	);

	const toggleCourse = (id: string) => {
		setExpandedCourseId((prev) => (prev === id ? null : id));
	};

	return (
		<Modal
			visible={visible}
			onCancel={onClose}
			className={styles.modal}
			footer={
				<div className={styles.footer}>
					<Button type="primary" onClick={onClose}>
						Close
					</Button>
				</div>
			}
			title={
				<div className={styles['title-container']}>
					<div className={styles.title}>{title}</div>
					<div className={styles['sub-title']}>ID: #{courseId}</div>
				</div>
			}
			width={500}
		>
			<div className={styles.banner}>
				<img
					src={bannerImg}
					className={styles.img}
					alt="banner-image"
				/>
			</div>

			<div className={styles.description}>
				<div className={styles.heading}>Description</div>
				<div className={styles.desc}>{description}</div>
			</div>

			<div className={styles.meta}>
				<MetaInfoCard
					title="Run time"
					icon={<Icon type="durationIcon" role="img" />}
					value={runTime}
				/>
				<MetaInfoCard
					title="Created"
					icon={<Icon type="createdIcon" role="img" />}
					value={created}
				/>
			</div>

			<div className={styles.languages}>
				<div>Available languages</div>
				<div className={styles.wrapper}>
					{languages.map((lang) => (
						<LanguageTag lang={lang} />
					))}
				</div>
			</div>

			<div className={styles.courses}>
				<div className={styles.title}>Courses ({courses.length})</div>

				<div className={styles.courseList}>
					{courses.map((course) => (
						<Accordion
							course={course}
							expandedCourseId={expandedCourseId}
							toggleCourse={toggleCourse}
						/>
					))}
				</div>
			</div>
		</Modal>
	);
};

export default CourseDetailModal;
